import {ApiClient} from '@hconnect/apiclient'
import {StoppageKind} from '@hconnect/common/types'
import {DateRange} from '@hconnect/uikit'
import {useUpmPlantTimezone} from '@hconnect/upm'
import {useQuery, UseQueryOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'
import {useEffect} from 'react'

import {AggregatedStoppageStatisticsResponse, TimeStep} from '../types'
import {prepareAggregatedStoppageCodePayload} from '../utils'

const QueryKey = 'aggregated-stoppage-statistics'

type Options<TSelect> = Omit<
  UseQueryOptions<AggregatedStoppageStatisticsResponse, AxiosError, TSelect>,
  'queryKey' | 'queryFn'
> & {
  onError?: (error: AxiosError) => void
}

export const useAggregatedStoppageStatistics = <TSelect = AggregatedStoppageStatisticsResponse>(
  plantId: string,
  payload: DateRange<Date> & {
    stoppedMainEquipments?: string[]
    mainEquipmentNumbers?: string[]
    equipmentNumbers?: string[]
    kinds?: StoppageKind[]
    stoppageCodes?: string[]
    timeStep: TimeStep
  },
  options?: Options<TSelect>
) => {
  const timezone = useUpmPlantTimezone(plantId)

  const query = useQuery<AggregatedStoppageStatisticsResponse, AxiosError, TSelect>({
    queryKey: [QueryKey, plantId, payload, timezone],
    queryFn: async ({signal}) => {
      const response = await ApiClient.axiosInstance.get<AggregatedStoppageStatisticsResponse>(
        `/downtime-dashboard/${plantId}/stoppages/time-period-statistics`,
        {
          params: prepareAggregatedStoppageCodePayload({
            to: payload.to,
            from: payload.from,
            stoppedDepartmentUpmIds: payload.stoppedMainEquipments,
            mainEquipmentNumbers: payload.mainEquipmentNumbers,
            equipmentNumbers: payload.equipmentNumbers,
            kinds: payload.kinds,
            stoppageCodes: payload.stoppageCodes,
            timeStep: payload.timeStep
          }),
          signal
        }
      )

      return response.data
    },
    staleTime: 1000 * 60 * 15,
    ...options
  })

  const onError = options?.onError
  useEffect(() => {
    if (query.error) {
      onError?.(query.error)
    }
  }, [query.error, onError])

  return query
}
